import { Item } from "./Item";
import { LooseAutocomplete, ValueOf } from "./Util";
import { S25WsNode } from "./S25WsNode";
import { Proto } from "./Proto";
import CharBoolean = Proto.CharBoolean;
import NumericalString = Proto.NumericalString;
import LowerStringBoolean = Proto.LowerStringBoolean;
import { Doc } from "../modules/system-settings/document-management/s25.document.const";

export namespace Report {
    export interface Data {
        subject: "Report";
        id: Item.Ids.Report;
    }

    export interface SimpleObject {
        rpt_name?: string;
        rpt_use?: Use;
        rpt_id: number;
        rpt_engine: Engine;
        object_type?: ObjectType;
        document_id?: number;
        optUUID?: number;
        itemID?: number;
        type?: Doc.TypeId;
        isAsync?: boolean;
    }

    export type Object = S25WsNode & {
        favorite: CharBoolean;
        href: string;
        last_mod_dt: string;
        last_mod_user: string;
        object_type: ObjectType;
        report_engine: Engine;
        report_filename: string;
        report_group_id: NumericalString | number;
        report_id: NumericalString | number;
        report_name: string;
        document_id?: number;
        content_disposition?: string;
        itemId?: number | NumericalString;
        itemName?: string;
    };

    export type Meta = S25WsNode & {
        async: LowerStringBoolean;
        content_disposition: Object["content_disposition"];
        mime_type: string;
        report_run: Run[] & { [key: string]: any };
        pipeline: LooseAutocomplete<"PDF">;
        favorite: Object["favorite"];
        object_type: Object["object_type"];
        report_engine: Object["report_engine"];
        report_filename: Object["report_filename"];
        report_group_id: Object["report_group_id"];
        report_id: Object["report_id"];
        report_name: Object["report_name"];
        parameter?: Parameter[];
        rpt_name?: Object["report_name"];
        document_id?: Object["document_id"];
    };

    export type Run = S25WsNode & {
        false_value: string;
        true_value: string;
        report_run_id: NumericalString;
        rpt_run_name: string;
    };

    export type Context = {
        num_parm1?: any;
        num_parm2?: NumericalString;
        num_parm3?: NumericalString;
        num_parm4?: NumericalString;
        char_parm4?: string;

        [key: string]: any;
    };

    export type Parameter = S25WsNode & {
        parameter_field_mapping: string;
        parameter_name: string;
        parameter_number: string;
        parameter_type: ParameterType;
        required: CharBoolean;
        sort_order: NumericalString;
        validate?: string;
        validate_type?: ParameterValidateType;
    };

    export type ParameterValue = string | number;

    export type Engine = "JR" | "WS" | "DM";

    export const Use = {
        None: 0,
        Confirmation: 1,
        Invoice: 2,
        Payment: 4,
    } as const;
    export type Use = ValueOf<typeof Use>;

    export const ObjectType = {
        NonSpecific: 0,
        Event: 1,
        Organization: 2,
        Contact: 3,
        Space: 4,
        Resource: 6,
        EventDocument: 10,
        PaymentDocument: 11,
        ReservationDocument: 12,
        OrganizationDocument: 13,
        EventListingDocument: 14,
        ReservationListingDocument: 15,
        LocationListingDocument: 16,
        ResourceListingDocument: 17,
        OrganizationListingDocument: 18,
        InvoiceExcel: 19,
        TaskListing: 20,
        Reservations: 21,
    } as const;
    export type ObjectType = ValueOf<typeof ObjectType>;

    export const Reports = {
        LocationDailySheet: { id: -147, object_type: ObjectType.Space },
        InvoiceRelated: { id: -149, object_type: ObjectType.Event },
        EventListing: { id: -171, object_type: ObjectType.EventListingDocument },
        LocationListing: { id: -179, object_type: ObjectType.LocationListingDocument },
        ResourceListing: { id: -180, object_type: ObjectType.ResourceListingDocument },
        OrganizationListing: { id: -181, object_type: ObjectType.OrganizationListingDocument },
        EventShell: { id: -198, object_type: ObjectType.Event },
        DefaultInvoice: { id: -199, object_type: ObjectType.Event },
        ReservationShell: { id: -210, object_type: ObjectType.ReservationDocument },
        OrganizationShell: { id: -217, object_type: ObjectType.OrganizationDocument },
        PaymentShell: { id: -236, object_type: ObjectType.PaymentDocument },
        EventListingShell: { id: -228, object_type: ObjectType.EventListingDocument },
        ReservationListingShell: { id: -229, object_type: ObjectType.ReservationListingDocument },
        LocationListingShell: { id: -230, object_type: ObjectType.LocationListingDocument },
        ResourceListingShell: { id: -231, object_type: ObjectType.ReservationListingDocument },
        OrganizationListingShell: { id: -232, object_type: ObjectType.OrganizationDocument },
        InvoiceShell: { id: -237, object_type: ObjectType.InvoiceExcel },
        TaskListing: { id: -238, object_type: ObjectType.TaskListing },
        ReservationsShell: { id: -240, object_type: ObjectType.Reservations },
    } as const;

    export const reportsById = new Map(Object.values(Reports).map((report) => [report.id, report]));

    // Maps task queries from search page to names accepted by report service
    export const taskQueryMap = {
        overdue: "overdue",
        outstanding: "outstanding",
        flagged: "flagged",
        completed: "completed",
        assigned_all: "assigned_by_you",
        today_agenda: "todays_agenda",
    } as const;

    export const enum ReportFormat {
        PDF = 2,
        Excel = 4,
        RichText = 6,
        HTML = 1,
        Text = 3,
    }

    export type ParameterType = ValueOf<typeof parameterType>;
    export const parameterType = {
        String: "S",
        Integer: "N",
        Float: "F",
        Date: "D",
        Time: "T",
        DateTime: "E",
        Boolean: "B",
        EventSearch: "QE",
        OrganizationSearch: "QO",
        LocationSearch: "QS",
        ResourceSearch: "QR",
        Object: "O",
        GroupBy: "GB",
        MeetingPatterns: "MP",
    } as const;

    export type ParameterValidateType = ValueOf<typeof parameterValidateType>;
    export const parameterValidateType = {
        GroupBy: "Group By",
    } as const;
}
